import { CommonModule } from '@angular/common';
import { Component, computed, inject, Injector, TemplateRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationExtras, Router } from '@angular/router';
import {
  BottomOutletService,
  BottomTabGroupService,
  Command,
  CommandStore,
  MenuItemService,
  SOFTLINE_FEATURE_COMMANDS,
} from '@softline/application';
import { Store } from '@softline/core';
import { UiCoreModule } from '@softline/ui-core';
import {
  asapScheduler,
  distinctUntilChanged,
  mergeAll,
  mergeMap,
  Observable,
  observeOn,
  of,
  take,
  withLatestFrom,
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MenuOutletComponent } from '../../../menu/menu-outlet/menu-outlet.component';

@Component({
  selector: 'soft-bottom-bar',
  imports: [CommonModule, UiCoreModule, MenuOutletComponent],
  templateUrl: './bottom-bar.component.html',
  styleUrls: ['./bottom-bar.component.css'],
})
export class BottomBarComponent {
  TemplateRef = TemplateRef;

  injector = inject(Injector);

  menuItemsService = inject(MenuItemService);
  bottomOutletService = inject(BottomOutletService);
  bottomTabGroupService = inject(BottomTabGroupService);

  bottomCommands$: Observable<readonly Command[]> = this.store
    .observe(SOFTLINE_FEATURE_COMMANDS, CommandStore.getters.commands, 'bottom-menu')
    .pipe(observeOn(asapScheduler));

  readonly hasBottomMenuItems = computed(() => {
    const items = this.menuItemsService.menuItems();
    const menuItems = items.flat().filter((item) => {
      if (typeof item.outlet === 'string')
        return item.outlet === 'bottom' && (item.visible ?? true);
      return item.outlet?.includes('bottom') && (item.visible ?? true);
    });
    return menuItems.length > 0;
  });

  readonly anyCommandVisible = toSignal(
    this.bottomCommands$.pipe(
      switchMap((commands) => {
        return of(commands).pipe(
          mergeMap((command) => {
            return command.map((cmd) => {
              const isVisible$ = cmd?.isVisible ? cmd.isVisible : of(true);

              return isVisible$.pipe(
                withLatestFrom(
                  ...commands.map((o) =>
                    o.isVisible ? o.isVisible.pipe(take(1)) : of(true)
                  )
                ),
                map((values) => values.some((o) => o))
              );
            });
          }),
          mergeAll(),
          distinctUntilChanged()
        );
      })
    ),
    { initialValue: false }
  );

  constructor(
    private store: Store,
    private router: Router
  ) {}

  onClick(
    execute: (() => void) | undefined,
    routerLink: any[] | string | undefined,
    canExecute: boolean,
    routerLinkParams: NavigationExtras | undefined
  ): void {
    if (!canExecute) return;
    if (execute) execute();
    else if (routerLink)
      this.router.navigate(
        typeof routerLink === 'string' ? [routerLink] : routerLink,
        routerLinkParams
      );
  }
}
